import React, {useEffect} from 'react';
import {Breadcrumbs as MuiBreadcrumbs} from '@mui/material';
import {Body, Icon, designSystemToken} from '@lightricks/react-design-system';
import setDocumentTitle from '@/utils/setDocumentTitle';
import SOCIAL_NETWORK_ICON from '@/config/socialNetworkIconMap';
import Link from '@/components/link';
import {ContentCampaignIcon} from '@/components/unusual-icons/UnusualIcons';
import styles from './Breadcrumbs.module.scss';

export type BreadcrumbItem = {
  label: string;
  link?: string;
  campaignNetwork?: string | null;
};

export interface BreadcrumbsProps {
  testID?: string;
  items: BreadcrumbItem[];
  enableItemLink?: boolean;
  separator?: React.ReactNode;
  updateDocumentTitle?: boolean;
}

const arrowStyle = {
  width: '20px',
  height: '20px',
};

function renderCampaignNetworkIcon(
  campaignNetwork: BreadcrumbItem['campaignNetwork']
) {
  if (campaignNetwork === undefined) {
    return null;
  }

  const iconName = campaignNetwork
    ? SOCIAL_NETWORK_ICON[campaignNetwork.toLowerCase()]
    : undefined;

  if (iconName) {
    return (
      <span aria-hidden="true" className={styles.trailingIcon}>
        <Icon
          name={iconName}
          size="medium"
          appearance="neutral"
          color="currentColor"
        />
      </span>
    );
  }

  return (
    <span aria-hidden="true" className={styles.trailingIcon}>
      <ContentCampaignIcon width={24} height={24} color="currentColor" />
    </span>
  );
}

function renderItemContent(
  item: BreadcrumbItem,
  labelColor: 'currentcolor' | 'primary'
) {
  const textColor =
    labelColor === 'primary'
      ? designSystemToken('semantic.fg.primary')
      : undefined;

  const label = (
    <Body size="md" color="currentcolor" className={styles.breadcrumbLabel}>
      {item.label}
    </Body>
  );

  const trailingIcon = renderCampaignNetworkIcon(item.campaignNetwork);

  if (!trailingIcon) {
    return textColor ? (
      <Body
        size="md"
        className={styles.breadcrumbLabel}
        style={{color: textColor}}
      >
        {item.label}
      </Body>
    ) : (
      label
    );
  }

  return (
    <span
      className={styles.labelWithIcon}
      style={textColor ? {color: textColor} : undefined}
    >
      {label}
      {trailingIcon}
    </span>
  );
}

function Breadcrumbs({
  testID = 'breadcrumbs',
  items,
  enableItemLink = true,
  updateDocumentTitle = true,
  separator = (
    <Icon
      name="Actions-Arrow-Forward-Small"
      size="medium"
      appearance="neutral"
      color={designSystemToken('semantic.fg.tertiary')}
      style={arrowStyle}
    />
  ),
}: BreadcrumbsProps) {
  useEffect(() => {
    if (!updateDocumentTitle) {
      return;
    }
    setDocumentTitle(...items.map((item) => item.label));
  }, [updateDocumentTitle, items]);

  return (
    <MuiBreadcrumbs
      data-testid={testID}
      separator={separator}
      classes={{ol: styles.list, separator: styles.separator}}
    >
      {items.map((item, index) => {
        const isLast = index === items.length - 1;

        if (enableItemLink && item.link && !isLast) {
          return (
            <Link key={item.label} to={item.link} className={styles.link}>
              {renderItemContent(item, 'currentcolor')}
            </Link>
          );
        }

        return (
          <span key={item.label}>{renderItemContent(item, 'primary')}</span>
        );
      })}
    </MuiBreadcrumbs>
  );
}

export default Breadcrumbs;
